import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { style } from './click-through-menu.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { Navigation, NavigationItem } from 'components/discovery/navigation-menu/typing/navigation-types.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { BaseUrlController } from 'components/shared/enterprise-domains/base-url-controller.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import 'components/discovery/navigation-menu/components/navigation-menu-column/navigation-menu-column.js'
import 'components/discovery/navigation-menu/components/secondary-nav/secondary-nav.js'

initializeIconsByRole({
  'csc-click-through-menu.more': { name: 'angle-right', style: 'regular' },
  'csc-click-through-menu.back': { name: 'arrow-left', style: 'regular' }
})

export const linkPosition = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const
export type LinkPositionTypes = typeof linkPosition

export const allCategoryLinkType = ['link', 'button'] as const
export type AllCategoryLinkTypes = typeof allCategoryLinkType

export const alignmentOptions = ['horizontal', 'vertical'] as const
export type alignmentOptionsTypes = typeof alignmentOptions

export const clickThroughMenuHiddenFields = ['image'] as const

@customElement('csc-click-through-menu')
export class CSCClickThroughMenu extends HiddenFieldsMixin(LitElement, clickThroughMenuHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  private urls = new BaseUrlController(this)

  @property({ type: Array, attribute: 'nav-items' })
  navItems: Navigation = []

  @property({ type: Boolean, attribute: 'all-category-link' })
  allCategoryLink = false

  @property({ type: String, attribute: 'link-position' })
  linkPosition: LinkPositionTypes[number] = 'top-left'

  @property({ type: Boolean })
  sidebar: boolean = false

  @property({ type: Boolean, attribute: 'show-more-icon' })
  showMoreIcon: boolean = false

  @property({ type: String, attribute: 'all-category-link-type' })
  allCategoryLinkType: AllCategoryLinkTypes[number] = 'link'

  @property({ type: String, attribute: 'all-category-link-variant' })
  allCategoryLinkVariant: string = 'primary'

  @property({ type: String, attribute: 'default-image' })
  defaultImage: string = ''

  @property({ type: String })
  image: string = ''

  @property({ type: String })
  alignment: alignmentOptionsTypes[number] = 'horizontal'

  @state()
  private currentLevelItems: Navigation = []

  @state()
  private breadcrumb: Navigation = []

  @state()
  private activeSidebarItem: NavigationItem | null | undefined = null

  private get currentImage(): string | null {
    const activeItems = [this.activeSidebarItem, ...this.breadcrumb]
    for (let i = activeItems.length - 1; i >= 0; i--) {
      const image = activeItems[i]?.menu_picture
      if (image) {
        return image
      }
    }
    return this.image || null
  }

  override connectedCallback(): void {
    super.connectedCallback()
    if (!this.sidebar && this.navItems.length > 0) {
      this.currentLevelItems = this.navItems
    }
  }

  override firstUpdated(): void {
    if (this.sidebar && this.navItems.length > 0) {
      this.activeSidebarItem = this.navItems[0]
      this.currentLevelItems = this.activeSidebarItem?.childs || []
    }
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    if (changedProperties.has('navItems')) {
      if (this.sidebar) {
        if (!this.activeSidebarItem && this.navItems.length > 0) {
          this.activeSidebarItem = this.navItems[0]
          this.currentLevelItems = this.activeSidebarItem?.childs || []
          this.breadcrumb = []
        } else if (this.activeSidebarItem) {
          const foundItem = this.findItemInNavigation(this.navItems, this.activeSidebarItem.id)
          if (foundItem) {
            this.activeSidebarItem = foundItem
            if (this.breadcrumb.length > 0) {
              const newBreadcrumb: Navigation = []
              let currentPath: NavigationItem | undefined = this.activeSidebarItem
              for (const breadcrumbItem of this.breadcrumb) {
                const nextChild: NavigationItem | undefined = currentPath?.childs?.find(child => child.id === breadcrumbItem.id)
                if (nextChild) {
                  newBreadcrumb.push(nextChild)
                  currentPath = nextChild
                } else {
                  break
                }
              }
              this.breadcrumb = newBreadcrumb
              this.currentLevelItems = currentPath?.childs || []
            } else {
              this.currentLevelItems = this.activeSidebarItem.childs || []
            }
          } else {
            this.activeSidebarItem = this.navItems.length > 0 ? this.navItems[0] : null
            this.currentLevelItems = this.activeSidebarItem?.childs || []
            this.breadcrumb = []
          }
        }
      } else {
        if (this.breadcrumb.length === 0) {
          this.currentLevelItems = this.navItems
        } else {
          const lastBreadcrumbItem = this.breadcrumb[this.breadcrumb.length - 1]
          if (lastBreadcrumbItem) {
            const foundItem = this.findItemInNavigation(this.navItems, lastBreadcrumbItem.id)
            if (foundItem && foundItem.childs) {
              this.currentLevelItems = foundItem.childs
            } else {
              this.breadcrumb = []
              this.currentLevelItems = this.navItems
            }
          } else {
            this.currentLevelItems = this.navItems
          }
        }
      }
    }

    if (changedProperties.has('sidebar')) {
      if (this.sidebar) {
        if (this.navItems.length > 0 && !this.activeSidebarItem) {
          this.activeSidebarItem = this.navItems[0]
          this.currentLevelItems = this.activeSidebarItem?.childs || []
          this.breadcrumb = []
        } else if (this.activeSidebarItem) {
          this.currentLevelItems = this.activeSidebarItem.childs || []
          this.breadcrumb = []
        }
      } else {
        this.activeSidebarItem = null
        this.currentLevelItems = this.navItems
        this.breadcrumb = []
      }
    }
  }

  private findItemInNavigation(navigation: Navigation, id: number): NavigationItem | undefined {
    for (const item of navigation) {
      if (item.id === id) {
        return item
      }
      if (item.childs) {
        const foundChild = this.findItemInNavigation(item.childs, id)
        if (foundChild) return foundChild
      }
    }
    return undefined
  }

  private handleCategorySelected(event: CustomEvent<NavigationItem>): void {
    const item = event.detail
    if (item.childs && item.childs.length > 0) {
      this.breadcrumb = [...this.breadcrumb, item]
      this.currentLevelItems = item.childs
    }
  }

  private handleBackClick(): void {
    if (this.breadcrumb.length > 0) {
      this.breadcrumb.pop()
    }

    if (this.breadcrumb.length > 0) {
      const lastBreadcrumbItem = this.breadcrumb[this.breadcrumb.length - 1]
      this.currentLevelItems = lastBreadcrumbItem?.childs || []
    } else {
      if (this.sidebar && this.activeSidebarItem) {
        this.currentLevelItems = this.activeSidebarItem.childs || []
      } else {
        this.currentLevelItems = this.navItems
      }
    }
  }

  private _handleSidebarItemSelect(event: CustomEvent<NavigationItem>): void {
    this.activeSidebarItem = event.detail
    this.currentLevelItems = this.activeSidebarItem?.childs || []
    this.breadcrumb = []
  }

  private handleBreadcrumbClick(event: CustomEvent<NavigationItem>, index: number): void {
    const clickedItem = event.detail
    this.breadcrumb = this.breadcrumb.slice(0, index + 1)
    this.currentLevelItems = clickedItem.childs || []
  }

  private renderAllCategoryLink(): TemplateResult | typeof nothing {
    if (!this.allCategoryLink) {
      return nothing
    }

    let activeItem: NavigationItem | undefined | null
    let linkUrl: string | undefined

    if (this.sidebar) {
      activeItem = this.activeSidebarItem
      if (activeItem) {
        if (this.breadcrumb.length > 0) {
          const deepestBreadcrumbItem = this.breadcrumb[this.breadcrumb.length - 1]
          linkUrl = this.urls.path(`${deepestBreadcrumbItem?.slug}/${deepestBreadcrumbItem?.id}/`)
        } else {
          linkUrl = this.urls.path(`${activeItem.slug}/${activeItem.id}/`)
        }
      }
    } else {
      if (this.breadcrumb.length === 0) {
        return nothing
      }
      activeItem = this.breadcrumb[this.breadcrumb.length - 1]
      if (activeItem) {
        linkUrl = this.urls.path(`${activeItem.slug}/${activeItem.id}/`)
      }
    }

    if (!activeItem || !linkUrl) {
      return nothing
    }

    const linkText = t('all_products_in_category', { ns: 'csc-click-through-menu', default: 'See all products in this category' })

    if (this.allCategoryLinkType === 'link') {
      return html`
        <div class="all-category-link-wrapper ${this.linkPosition}">
          <cse-link url="${linkUrl}" variant="primary">
            <cse-icon icon-role="csc-click-through-menu.more" slot="prefix"></cse-icon>
            ${linkText}
          </cse-link>
        </div>
      `
    } else {
      return html`
        <div class="all-category-link-wrapper ${this.linkPosition}">
          <cse-button href="${linkUrl}" variant="${this.allCategoryLinkVariant}">
            <cse-icon icon-role="csc-click-through-menu.more" slot="prefix"></cse-icon>
            ${linkText}
          </cse-button>
        </div>
      `
    }
  }

  private renderBreadcrumbPath(): TemplateResult | typeof nothing {
    if (this.breadcrumb.length === 0 && !this.sidebar) {
      return nothing
    }

    let fullPathItems: NavigationItem[] = []
    if (this.sidebar && this.activeSidebarItem) {
      fullPathItems.push(this.activeSidebarItem)
    }
    fullPathItems = [...fullPathItems, ...this.breadcrumb]

    return html`
      <div class="breadcrumb-path">
        ${fullPathItems.map((item, index) => html`
          ${index > 0 ? html`<span class="breadcrumb-separator"> / </span>` : nothing}
          ${index === fullPathItems.length - 1 ?
            html`<cse-text class="current-breadcrumb">${item.name}</cse-text>` :
            html`
              <cse-link
                class="breadcrumb-link"
                variant="secondary"
                @click="${() => this.handleBreadcrumbClick(new CustomEvent('breadcrumb-click', { detail: item }), index)}"
              >
                ${item.name}
              </cse-link>
            `
          }
        `)}
      </div>
    `
  }

  private renderImage(): TemplateResult | typeof nothing {
    if (this.isHidden('image') || !this.currentImage) {
      return nothing
    }

    return html`
      <div class="image-wrapper">
        <cse-image
          source="${this.currentImage}"
          fallback-source="${this.defaultImage}"
          loading="lazy"
          fit="cover"
        ></cse-image>
      </div>
    `
  }

  override render(): TemplateResult {
    const gridMenuClass = 'grid-menu'
    const gridItemsContainerClass = `grid-items-container grid-layout ${this.alignment}`

    return this.sidebar ? html`
      <div class="container sidebar-layout">
        <csc-navigation-menu-column
          navigation-items="${JSON.stringify(this.navItems)}"
          active-item-id="${this.activeSidebarItem?.id || null}"
          default-image="${this.defaultImage}"
          @item-select="${this._handleSidebarItemSelect}"
        ></csc-navigation-menu-column>
        <div class="wrapper main-content">
          ${this.activeSidebarItem ? html`
            <div class="content">
              <div class="main">
                ${(this.linkPosition === 'top-left' || this.linkPosition === 'top-right') ?
                  this.renderAllCategoryLink() :
                  nothing}

                ${this.breadcrumb.length > 0 || this.sidebar ? html`
                    <div class="back-navigation-section">
                      ${this.breadcrumb.length > 0 ? html`
                        <cse-link class="back-button" @click="${this.handleBackClick}">
                          <cse-icon icon-role="csc-click-through-menu.back" slot="prefix"></cse-icon>
                          ${t('back', { ns: 'csc-click-through-menu', default: 'Go back' })}
                        </cse-link>
                      ` : nothing}
                      ${this.renderBreadcrumbPath()}
                    </div>
                ` : nothing}

                <div class="${gridMenuClass}">
                  <div class="${gridItemsContainerClass}">
                    ${this.currentLevelItems.filter(child => !child.hidden).map(child => html`
                      <csc-secondary-nav
                        .navigation="${child}"
                        ?enable-click-through="${true}"
                        .showAllLink="${false}"
                        ?show-more-icon="${this.showMoreIcon}"
                        @category-selected="${this.handleCategorySelected}"
                      ></csc-secondary-nav>
                    `)}
                  </div>
                </div>
                ${(this.linkPosition === 'bottom-left' || this.linkPosition === 'bottom-right') ? this.renderAllCategoryLink() : nothing}
              </div>
              ${this.renderImage()}
            </div>
          ` : nothing }
        </div>
      </div>
    ` :
      html`
      <div class="container">
        ${(this.linkPosition === 'top-left' || this.linkPosition === 'top-right') && this.breadcrumb.length > 0 ? this.renderAllCategoryLink() : nothing}
        <div class="wrapper">
          <div class="content">
            <div class="main">
              ${this.breadcrumb.length > 0 ? html`
                  <div class="back-navigation-section">
                    <cse-link class="back-button" @click="${this.handleBackClick}">
                      <cse-icon icon-role="csc-click-through-menu.back" slot="prefix"></cse-icon>
                      ${t('back', { ns: 'csc-click-through-menu', default: 'Go back' })}
                    </cse-link>
                    ${this.renderBreadcrumbPath()}
                  </div>
              ` : nothing}
              <div class="${gridMenuClass}">
                <div class="${gridItemsContainerClass}">
                  ${this.currentLevelItems.filter(child => !child.hidden).map(item => html`
                    <csc-secondary-nav
                      .navigation="${item}"
                      ?enable-click-through="${true}"
                      .showAllLink="${false}"
                      ?show-more-icon="${this.showMoreIcon}"
                      @category-selected="${this.handleCategorySelected}"
                    ></csc-secondary-nav>
                  `)}
                </div>
              </div>
              ${(this.linkPosition === 'bottom-left' || this.linkPosition === 'bottom-right') && this.breadcrumb.length > 0 ? this.renderAllCategoryLink() : nothing}
            </div>
            ${this.renderImage()}
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-click-through-menu': CSCClickThroughMenu
  }
}
